import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Empty,
  Input,
  List,
  Space,
  Spin,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
  InboxOutlined,
  PaperClipOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { attachmentApi } from '../../../api/workorder';
import './WorkOrderAttachmentDrawer.css';

const { Dragger } = Upload;
const { Text } = Typography;

const getAttachmentName = (file) => file?.fileName || file?.FileName || file?.file_name || file?.filename || 'Unnamed file';
const getAttachmentUrl = (file) => file?.ossUrl || file?.OSSUrl || file?.oss_url || file?.url || file?.fileUrl || '';

const getFileExtension = (file) => {
  const name = getAttachmentName(file).toLowerCase();
  const fileType = String(file?.fileType || '').toLowerCase();
  return (fileType.includes('/') ? fileType.split('/').pop() : fileType) || name.split('.').pop() || '';
};

const isImage = (file) => ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(getFileExtension(file));
const isPdf = (file) => getFileExtension(file) === 'pdf';

const formatFileSize = (size) => {
  if (!size || Number.isNaN(Number(size))) return '--';
  const value = Number(size);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const getResponseData = (response) => {
  const data = response?.data?.data ?? response?.data;
  return Array.isArray(data) ? data : [];
};

const WorkOrderAttachmentDrawer = ({ open, hblNo, onClose }) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadAttachments = useCallback(async () => {
    if (!hblNo) return;
    setLoading(true);
    try {
      const response = await attachmentApi.getList(hblNo);
      const files = getResponseData(response);
      setUploadedFiles(files);
      setSelectedFile((current) => {
        if (current && files.some((file) => file.id === current.id)) return current;
        return files[0] || null;
      });
    } catch (error) {
      messageApi.error(error.response?.data?.message || 'Failed to load attachments');
      setUploadedFiles([]);
      setSelectedFile(null);
    } finally {
      setLoading(false);
    }
  }, [hblNo, messageApi]);

  useEffect(() => {
    if (open) {
      setPendingFiles([]);
      loadAttachments();
    } else {
      setPendingFiles([]);
      setSelectedFile(null);
    }
  }, [open, loadAttachments]);

  const pendingUploadFileList = useMemo(() => pendingFiles.map((item) => ({
    uid: item.uid,
    name: item.file.name,
    status: 'done',
  })), [pendingFiles]);

  const handleBeforeUpload = (file) => {
    setPendingFiles((current) => [
      ...current,
      { uid: file.uid, file, remark: '' },
    ]);
    return false;
  };

  const handleRemovePendingFile = (uid) => {
    setPendingFiles((current) => current.filter((item) => item.uid !== uid));
  };

  const handleRemarkChange = (uid, remark) => {
    setPendingFiles((current) => current.map((item) => (
      item.uid === uid ? { ...item, remark } : item
    )));
  };

  const handleUpload = async () => {
    if (!hblNo) {
      messageApi.error('HBL No. is required');
      return;
    }
    if (pendingFiles.length === 0) {
      messageApi.warning('Please select at least one file');
      return;
    }

    const formData = new FormData();
    formData.append('hblNo', hblNo);
    pendingFiles.forEach(({ file, remark }) => {
      formData.append('files', file);
      formData.append('remarks', remark.trim());
    });

    setUploading(true);
    try {
      const response = await attachmentApi.uploadBatch(formData);
      if (response?.data?.code !== 200) {
        throw new Error(response?.data?.message || 'Attachment upload failed');
      }
      messageApi.success(response.data.message || 'Attachments uploaded successfully');
      setPendingFiles([]);
      await loadAttachments();
    } catch (error) {
      messageApi.error(error.response?.data?.message || error.message || 'Attachment upload failed');
    } finally {
      setUploading(false);
    }
  };

  const renderPreview = () => {
    if (!selectedFile) {
      return <Empty description="Select a file to preview" />;
    }

    const url = getAttachmentUrl(selectedFile);
    if (!url) {
      return <Empty description="Preview URL unavailable" />;
    }

    if (isImage(selectedFile)) {
      return <img className="workorder-attachment-preview-image" src={url} alt={getAttachmentName(selectedFile)} />;
    }

    if (isPdf(selectedFile)) {
      return <iframe className="workorder-attachment-preview-frame" src={url} title={getAttachmentName(selectedFile)} />;
    }

    return (
      <div className="workorder-attachment-download">
        <FileOutlined className="workorder-attachment-download-icon" />
        <Text>{getAttachmentName(selectedFile)}</Text>
        <Button type="primary" icon={<DownloadOutlined />} href={url} target="_blank" rel="noreferrer">
          Open / Download
        </Button>
      </div>
    );
  };

  return (
    <>
      {contextHolder}
      <Drawer
        title={`Attachments${hblNo ? ` - ${hblNo}` : ''}`}
        placement="right"
        width="86%"
        open={open}
        onClose={onClose}
        destroyOnClose
      >
        <div className="workorder-attachment-layout">
          <div className="workorder-attachment-left">
            <div className="workorder-attachment-upload-card">
              <Dragger
                multiple
                beforeUpload={handleBeforeUpload}
                fileList={pendingUploadFileList}
                onRemove={(file) => handleRemovePendingFile(file.uid)}
                showUploadList={false}
              >
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">Click or drag files to upload</p>
                <p className="ant-upload-hint">Multiple files are supported. Add an optional remark for each file.</p>
              </Dragger>

              {pendingFiles.length > 0 && (
                <div className="workorder-attachment-pending-list">
                  {pendingFiles.map(({ uid, file, remark }) => (
                    <div className="workorder-attachment-pending-item" key={uid}>
                      <Space className="workorder-attachment-pending-name">
                        <PaperClipOutlined />
                        <Text ellipsis={{ tooltip: file.name }}>{file.name}</Text>
                      </Space>
                      <Input
                        value={remark}
                        onChange={(event) => handleRemarkChange(uid, event.target.value)}
                        placeholder="Remark (optional)"
                        maxLength={255}
                      />
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemovePendingFile(uid)}
                      />
                    </div>
                  ))}
                  <Button type="primary" icon={<UploadOutlined />} loading={uploading} onClick={handleUpload}>
                    Upload {pendingFiles.length} file{pendingFiles.length > 1 ? 's' : ''}
                  </Button>
                </div>
              )}
            </div>

            {pendingFiles.length === 0 && (
              <>
                <div className="workorder-attachment-uploaded-header">
                  <Text strong>Uploaded files ({uploadedFiles.length})</Text>
                  <Button type="link" onClick={loadAttachments} loading={loading}>Refresh</Button>
                </div>
                <div className="workorder-attachment-file-list">
                  {loading ? <Spin /> : uploadedFiles.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No uploaded files" />
                  ) : (
                    <List
                      dataSource={uploadedFiles}
                      renderItem={(file) => (
                        <List.Item
                          className={selectedFile?.id === file.id ? 'workorder-attachment-file-item selected' : 'workorder-attachment-file-item'}
                          onClick={() => setSelectedFile(file)}
                        >
                          <List.Item.Meta
                            avatar={<FileOutlined />}
                            title={<Text ellipsis={{ tooltip: getAttachmentName(file) }}>{getAttachmentName(file)}</Text>}
                            description={file.remark || `${formatFileSize(file.fileSize)}${file.createTime ? ` · ${file.createTime}` : ''}`}
                          />
                        </List.Item>
                      )}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          <div className="workorder-attachment-preview">
            <div className="workorder-attachment-preview-header">
              <Text strong>{selectedFile ? getAttachmentName(selectedFile) : 'File preview'}</Text>
            </div>
            <div className="workorder-attachment-preview-body">{renderPreview()}</div>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default WorkOrderAttachmentDrawer;
